import React, { useState, useEffect } from 'react';
import { createMemoStyles, usePreviousState, uniqueId, safeRest } from '@wds/utils';
import { Typography } from '@wds/typography';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    type = theme.type,
    spacing = theme.spacing,
    motion = theme.motion,
    radius = theme.radius,
    shadow = theme.shadow;
  var radio = {
    bgColorOn: palette.primary.main,
    iconColorOn: palette.primary.contrast
  };
  var bounceFrames = {
    '@keyframes bounce': {
      '0%': {
        opacity: 0,
        transform: 'scale(0.6)',
        animationTimingFunction: 'cubic-bezier(0, 0, .1, 1)'
      },
      '10%': {
        opacity: 1
      },
      '33%': {
        opacity: 1,
        transform: 'scale(1.4)',
        animationTimingFunction: 'cubic-bezier(.2, 0, 0, 1)'
      },
      '100%': {
        opacity: 1,
        transform: 'scale(1)'
      }
    }
  };
  var scaleDownFrames = {
    '@keyframes scaleDown': {
      from: {
        opacity: 1,
        transform: 'scale(1)',
        animationTimingFunction: 'cubic-bezier(1, 0, 1, 1)'
      },
      '75%': {
        opacity: 1
      },
      '100%': {
        opacity: 0,
        transform: 'scale(0.4)'
      }
    }
  };
  return __assign(__assign(__assign({}, bounceFrames), scaleDownFrames), {
    wrenchRadio: function () {
      return {
        position: 'relative',
        display: 'inline-block',
        minHeight: spacing[5],
        minWidth: spacing[5],
        whiteSpace: 'nowrap'
      };
    },
    wrenchRadioFaux: function () {
      return {
        content: '""',
        display: 'inline-block',
        boxSizing: 'border-box',
        height: spacing[5],
        width: spacing[5],
        fontSize: type.fontSize.bodySm,
        border: "1px solid ".concat(palette.border.main),
        background: palette.background.light,
        borderRadius: radius.large,
        transition: "all 50ms ".concat(motion.curve.exit, " 100ms"),
        verticalAlign: 'top',
        textAlign: 'left',
        '&:after': {
          content: '""',
          opacity: 0,
          fontFamily: type.fontFamily.icon,
          color: radio.iconColorOn,
          boxSizing: 'border-box',
          width: spacing[2],
          height: spacing[2],
          borderRadius: radius.large,
          textAlign: 'center',
          position: 'absolute',
          animation: "$scaleDown 150ms forwards",
          background: radio.iconColorOn,
          top: '50%',
          left: '50%',
          marginLeft: -spacing[1],
          marginTop: -spacing[1]
        }
      };
    },
    wrenchRadioLabel: function () {
      return {
        padding: [0, 0, 0, spacing[4]],
        whiteSpace: 'normal',
        display: 'inline-block',
        wordBreak: 'break-word'
      };
    },
    wrenchRadioNative: function () {
      return {
        position: 'absolute',
        cursor: 'inherit',
        opacity: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        left: 0,
        '&:focus + $wrenchRadioFaux, &:hover + $wrenchRadioFaux': {
          borderColor: palette.border.hover,
          boxShadow: shadow.sky,
          transform: 'scale(1.1)',
          transition: "transform 200ms ".concat(motion.curve.ease, ", border-color 75ms linear 50ms")
        },
        '&:focus:checked + $wrenchRadioFaux': {
          borderColor: '#fff !important',
          boxShadow: "0 0 0 1px ".concat(palette.primary.main, " !important")
        },
        '&:active + $wrenchRadioFaux': {
          transform: 'scale(0.7)',
          transition: "transform 100ms ".concat(motion.curve.bounceStart)
        },
        '&:checked + $wrenchRadioFaux': {
          borderColor: radio.bgColorOn,
          background: radio.bgColorOn,
          transform: 'scale(1)',
          transition: "transform 200ms ".concat(motion.curve.bounceEnd),
          '&:after': {
            animation: "$bounce 200ms 50ms forwards"
          }
        },
        '&:disabled + $wrenchRadioFaux, &:disabled ~ $wrenchRadioLabel': {
          opacity: '0.5',
          transform: 'scale(1)'
        }
      };
    }
  });
});
var Radio = function (_a) {
  var ariaLabel = _a.ariaLabel,
    checked = _a.checked,
    _b = _a.defaultChecked,
    defaultChecked = _b === void 0 ? false : _b,
    _c = _a.disabled,
    disabled = _c === void 0 ? false : _c,
    id = _a.id,
    innerRef = _a.innerRef,
    label = _a.label,
    name = _a.name,
    onBlur = _a.onBlur,
    onChange = _a.onChange,
    value = _a.value,
    rest = __rest(_a, ["ariaLabel", "checked", "defaultChecked", "disabled", "id", "innerRef", "label", "name", "onBlur", "onChange", "value"]);
  var _d = useState(checked !== null && checked !== void 0 ? checked : defaultChecked),
    isChecked = _d[0],
    setChecked = _d[1];
  var _e = useState(false),
    isFocused = _e[0],
    setFocused = _e[1];
  /**
   * Use previous state to set the current state
   * This is a hack to make uncontrolled radio groups work
   */
  var prevIsChecked = usePreviousState(isChecked);
  /**
   * Set the checked state of radio
   * If checked value is passed, it takes precendent over defaultChecked to become
   * controlled input.  If no value is passed, it uses the defaultChecked value
   */
  useEffect(function () {
    setChecked(checked !== null && checked !== void 0 ? checked : defaultChecked);
  }, [checked, defaultChecked]);
  var inputID = id !== null && id !== void 0 ? id : uniqueId('input');
  var _f = useStyles({}),
    wrenchRadio = _f.wrenchRadio,
    wrenchRadioFaux = _f.wrenchRadioFaux,
    wrenchRadioLabel = _f.wrenchRadioLabel,
    wrenchRadioNative = _f.wrenchRadioNative;
  var handleBlur = function (e) {
    setFocused(!isFocused);
    e.persist();
    if (onBlur) onBlur(e);
  };
  var handleFocus = function (e) {
    setFocused(!isFocused);
    e.persist();
  };
  var handleChange = function (e) {
    setChecked(!prevIsChecked);
    e.persist();
    if (onChange) onChange(e);
  };
  return React.createElement("span", {
    className: wrenchRadio
  }, React.createElement("input", __assign({
    "aria-label": ariaLabel,
    "aria-checked": isChecked,
    /**
     * defaultChecked is set by either passing in the defaultChecked prop
     * OR passing in the checked prop without an onChange handler
     * If the checked property is passed with onChange, the checked property will be used
     */
    defaultChecked: defaultChecked || (checked && !onChange ? isChecked : undefined),
    /**
     * a controlled radio input is used by passing both a checked and onChange prop
     */
    checked: checked && onChange && isChecked,
    className: wrenchRadioNative,
    disabled: disabled,
    id: inputID,
    name: name,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onChange: handleChange,
    tabIndex: disabled ? -1 : 0,
    type: "radio",
    value: value,
    ref: innerRef
  }, safeRest(rest))), React.createElement("span", {
    className: wrenchRadioFaux,
    "aria-hidden": "true"
  }), label && React.createElement("span", {
    className: wrenchRadioLabel
  }, React.createElement("label", {
    htmlFor: inputID
  }, React.createElement(Typography, {
    variant: "body",
    component: "span"
  }, label))));
};
export { Radio };